import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Save,
  Upload,
  Plus,
  X,
  Loader2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const PortfolioForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [funds, setFunds] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    logo_alt: '',
    invested_month: new Date().getMonth() + 1,
    invested_year: new Date().getFullYear(),
    sectors: [],
    founders: [],
    fund_team_members: [],
    location: '',
    geography: '',
    website_url: '',
    patents_count: 0,
    fund_id: '',
    fund_ids: [],
    short_description: '',
    long_description: '',
    status: 'active',
    is_featured: false
  });

  const [newSector, setNewSector] = useState('');
  const [newFounder, setNewFounder] = useState({ name: '', linkedin_url: '' });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [fundsRes, teamRes] = await Promise.all([
        axios.get(`${API}/admin/funds`, { withCredentials: true }),
        axios.get(`${API}/admin/team`, { withCredentials: true })
      ]);
      setFunds(fundsRes.data);
      setTeamMembers(teamRes.data);

      if (isEditing) {
        const companyRes = await axios.get(`${API}/admin/portfolio/${id}`, { withCredentials: true });
        const company = companyRes.data || {};
        const normalizedFundIds = Array.isArray(company.fund_ids) && company.fund_ids.length
          ? company.fund_ids
          : (company.fund_id ? [company.fund_id] : []);
        setFormData({
          ...company,
          fund_ids: normalizedFundIds,
          fund_id: normalizedFundIds[0] || company.fund_id || '',
          sectors: Array.isArray(company.sectors) ? company.sectors : [],
          founders: Array.isArray(company.founders) ? company.founders : [],
          fund_team_members: Array.isArray(company.fund_team_members) ? company.fund_team_members : [],
        });
        if (companyRes.data.logo_url) {
          setLogoPreview(companyRes.data.logo_url.startsWith('/') 
            ? `${BACKEND_URL}${companyRes.data.logo_url}` 
            : companyRes.data.logo_url
          );
        }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [id, isEditing]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddSector = () => {
    if (newSector.trim() && !formData.sectors.includes(newSector.trim())) {
      setFormData(prev => ({
        ...prev,
        sectors: [...prev.sectors, newSector.trim()]
      }));
      setNewSector('');
    }
  };

  const handleRemoveSector = (sector) => {
    setFormData(prev => ({
      ...prev,
      sectors: prev.sectors.filter(s => s !== sector)
    }));
  };

  const handleAddFounder = () => {
    if (newFounder.name.trim()) {
      setFormData(prev => ({
        ...prev,
        founders: [...prev.founders, { ...newFounder }]
      }));
      setNewFounder({ name: '', linkedin_url: '' });
    }
  };

  const handleRemoveFounder = (index) => {
    setFormData(prev => ({
      ...prev,
      founders: prev.founders.filter((_, i) => i !== index)
    }));
  };

  const handleAddTeamMember = (memberId) => {
    if (memberId && !formData.fund_team_members?.includes(memberId)) {
      setFormData(prev => ({
        ...prev,
        fund_team_members: [...(prev.fund_team_members || []), memberId]
      }));
    }
  };

  const handleRemoveTeamMember = (memberId) => {
    setFormData(prev => ({
      ...prev,
      fund_team_members: (prev.fund_team_members || []).filter(id => id !== memberId)
    }));
  };

  const handleAddFund = (fundId) => {
    if (!fundId) return;
    setFormData(prev => {
      const nextFundIds = Array.from(new Set([...(prev.fund_ids || []), fundId]));
      return { ...prev, fund_ids: nextFundIds, fund_id: nextFundIds[0] || '' };
    });
  };

  const handleRemoveFund = (fundId) => {
    setFormData(prev => {
      const nextFundIds = (prev.fund_ids || []).filter(id => id !== fundId);
      return { ...prev, fund_ids: nextFundIds, fund_id: nextFundIds[0] || '' };
    });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const fundIds = Array.isArray(formData.fund_ids) ? formData.fund_ids.filter(Boolean) : [];
    if (!formData.name || fundIds.length === 0) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...formData,
        fund_ids: fundIds,
        fund_id: fundIds[0],
        logo_alt: formData.logo_alt?.trim() || null,
      };
      let response;
      if (isEditing) {
        response = await axios.put(`${API}/admin/portfolio/${id}`, submitData, { withCredentials: true });
      } else {
        response = await axios.post(`${API}/admin/portfolio`, submitData, { withCredentials: true });
      }

      const companyId = response.data.company_id;

      // Upload logo if selected
      if (logoFile) {
        const logoFormData = new FormData();
        logoFormData.append('file', logoFile);
        logoFormData.append('alt_text', formData.logo_alt || formData.name);
        await axios.post(`${API}/admin/portfolio/${companyId}/logo`, logoFormData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      toast.success(isEditing ? 'Company updated successfully' : 'Company created successfully');
      navigate('/admin/portfolio');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save company');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#C8473A]" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/portfolio')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-[#211F1F]">
            {isEditing ? 'Edit Company' : 'Add New Company'}
          </h1>
          <p className="text-gray-500">Fill in the company details</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl">
        <div className="bg-white rounded-lg border p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name">Company Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Enter company name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Funds *</Label>
              <p className="text-xs text-gray-500">Select one or more funds this company is associated with</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {(formData.fund_ids || []).map((fundId) => {
                  const fund = funds.find(f => f.fund_id === fundId);
                  return (
                    <Badge key={fundId} variant="secondary" className="gap-1 py-1.5">
                      {fund?.name || fundId}
                      <X
                        className="w-3 h-3 cursor-pointer"
                        onClick={() => handleRemoveFund(fundId)}
                      />
                    </Badge>
                  );
                })}
              </div>
              <Select onValueChange={handleAddFund}>
                <SelectTrigger>
                  <SelectValue placeholder="Add fund..." />
                </SelectTrigger>
                <SelectContent>
                  {funds
                    .filter(f => !(formData.fund_ids || []).includes(f.fund_id))
                    .map(fund => (
                      <SelectItem key={fund.fund_id} value={fund.fund_id}>
                        {fund.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Logo */}
          <div className="space-y-2">
            <Label>Company Logo</Label>
            <div className="flex items-start gap-4">
              {logoPreview && (
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="w-20 h-20 rounded-lg object-contain bg-gray-50 border"
                />
              )}
              <div>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="mb-2"
                />
                <Input
                  placeholder="Alt text for logo"
                  value={formData.logo_alt}
                  onChange={(e) => handleChange('logo_alt', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Investment Details */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Invested Month</Label>
              <Select 
                value={String(formData.invested_month)} 
                onValueChange={(v) => handleChange('invested_month', parseInt(v))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTH_NAMES.map((month, index) => (
                    <SelectItem key={index} value={String(index + 1)}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Invested Year</Label>
              <Input
                type="number"
                min="2000"
                max="2030"
                value={formData.invested_year}
                onChange={(e) => handleChange('invested_year', parseInt(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <Label>Patents Filed/Granted</Label>
              <Input
                type="number"
                min="0"
                value={formData.patents_count}
                onChange={(e) => handleChange('patents_count', parseInt(e.target.value) || 0)}
              />
            </div>
          </div>

          {/* Sectors */}
          <div className="space-y-2">
            <Label>Sectors</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.sectors.map(sector => (
                <Badge key={sector} variant="secondary" className="gap-1">
                  {sector}
                  <X
                    className="w-3 h-3 cursor-pointer"
                    onClick={() => handleRemoveSector(sector)}
                  />
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add sector"
                value={newSector}
                onChange={(e) => setNewSector(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSector())}
              />
              <Button type="button" variant="outline" onClick={handleAddSector}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Founders */}
          <div className="space-y-2">
            <Label>Founders</Label>
            <div className="space-y-2 mb-2">
              {formData.founders.map((founder, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                  <span className="flex-1">{founder.name}</span>
                  {founder.linkedin_url && (
                    <a href={founder.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600">
                      LinkedIn
                    </a>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveFounder(index)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Founder name"
                value={newFounder.name}
                onChange={(e) => setNewFounder(prev => ({ ...prev, name: e.target.value }))}
              />
              <Input
                placeholder="LinkedIn URL (optional)"
                value={newFounder.linkedin_url}
                onChange={(e) => setNewFounder(prev => ({ ...prev, linkedin_url: e.target.value }))}
              />
              <Button type="button" variant="outline" onClick={handleAddFounder}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Fund Team Members */}
          <div className="space-y-2">
            <Label>Fund Team Members</Label>
            <p className="text-xs text-gray-500 mb-2">Select YourNest team members involved with this company</p>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.fund_team_members?.map(memberId => {
                const member = teamMembers.find(m => m.member_id === memberId);
                return member ? (
                  <Badge key={memberId} variant="secondary" className="gap-1 py-1.5">
                    {member.name}
                    <X
                      className="w-3 h-3 cursor-pointer"
                      onClick={() => handleRemoveTeamMember(memberId)}
                    />
                  </Badge>
                ) : null;
              })}
            </div>
            <Select onValueChange={handleAddTeamMember}>
              <SelectTrigger>
                <SelectValue placeholder="Add team member..." />
              </SelectTrigger>
              <SelectContent>
                {teamMembers
                  .filter(m => !formData.fund_team_members?.includes(m.member_id))
                  .map(member => (
                    <SelectItem key={member.member_id} value={member.member_id}>
                      {member.name} - {member.designation}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Location, Geography & Website */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="e.g., Bangalore, India"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="geography">Geography</Label>
              <Input
                id="geography"
                value={formData.geography}
                onChange={(e) => handleChange('geography', e.target.value)}
                placeholder="e.g., India, US, Global"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="website_url">Website URL</Label>
              <Input
                id="website_url"
                type="url"
                value={formData.website_url}
                onChange={(e) => handleChange('website_url', e.target.value)}
                placeholder="https://example.com"
              />
            </div>
          </div>

          {/* Short Description */}
          <div className="space-y-2">
            <Label htmlFor="short_description">Short Description</Label>
            <Textarea
              id="short_description"
              value={formData.short_description}
              onChange={(e) => handleChange('short_description', e.target.value)}
              placeholder="Brief one-liner about the company (shown in portfolio list)"
              rows={2}
            />
            <p className="text-xs text-gray-500">This will be displayed in the portfolio grid view.</p>
          </div>

          {/* Long Description */}
          <div className="space-y-2">
            <Label htmlFor="long_description">Long Description</Label>
            <Textarea
              id="long_description"
              value={formData.long_description}
              onChange={(e) => handleChange('long_description', e.target.value)}
              placeholder="Detailed description of the company (shown in company popup/detail view)"
              rows={6}
            />
            <p className="text-xs text-gray-500">This will be displayed when viewing company details.</p>
          </div>

          {/* Status & Featured */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => handleChange('status', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="exited">Exited</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-3 pt-8">
              <Switch
                checked={formData.is_featured}
                onCheckedChange={(v) => handleChange('is_featured', v)}
              />
              <Label>Featured Company</Label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4 mt-6">
          <Button type="button" variant="outline" onClick={() => navigate('/admin/portfolio')}>
            Cancel
          </Button>
          <Button type="submit" className="bg-[#C8473A] hover:bg-[#B74F40]" disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEditing ? 'Update Company' : 'Create Company'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PortfolioForm;
